import { Check, Search, X } from "lucide-react";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import type { MapDocument, MapLocation } from "../shared/types";

const categoryLabels = {
  exhibitor: "Exhibitor",
  stage: "Stage",
  lounge: "Lounge",
  facility: "Facility",
  unassigned: "Booth",
};

function searchable(value: string) {
  return value
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/** A bounded, keyboard-accessible location search; every location is searchable. */
export function VenueMapSearch({
  document,
  selectedId,
  onSelect,
  compact = false,
  onOpen,
}: {
  compact?: boolean;
  onOpen?: () => void;
  document: MapDocument;
  selectedId?: string;
  onSelect: (location: MapLocation) => void;
}) {
  const [disclosed, setDisclosed] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const listId = `map-search-${useId().replace(/:/g, "")}`;
  const fieldId = `${listId}-field`;
  useLayoutEffect(() => {
    if (compact && disclosed) input.current?.focus({ preventScroll: true });
  }, [compact, disclosed]);
  const entries = useMemo(() => {
    const stands = new Map(
      document.geometry.stands.map((stand) => [stand.id, stand]),
    );
    return document.locations
      .filter(
        (location) => !location.hidden && !stands.get(location.id)?.hidden,
      )
      .map((location) => {
        const stand = stands.get(location.id);
        const name = stand?.name || stand?.shortName || location.label;
        return {
          location,
          name,
          label: stand ? `${stand.id} · ${name}` : name,
          detail: stand
            ? `${stand.id} · ${categoryLabels[stand.category]}`
            : "Location",
          id: searchable(location.id),
          text: searchable(
            [
              location.id,
              location.label,
              name,
              stand?.shortName || "",
              stand?.mapLabel || "",
            ].join(" "),
          ),
        };
      });
  }, [document]);
  const selectedEntry = entries.find(
    (entry) => entry.location.id === selectedId,
  );
  const matches = useMemo(() => {
    const normalized = searchable(query.trim());
    const words = normalized.split(/\s+/).filter(Boolean);
    const rank = (entry: (typeof entries)[number]) =>
      normalized && entry.id === normalized
        ? 0
        : normalized && entry.id.startsWith(normalized)
          ? 1
          : 2;
    return entries
      .filter((entry) => words.every((word) => entry.text.includes(word)))
      .sort(
        (a, b) =>
          rank(a) - rank(b) ||
          a.name.localeCompare(b.name, undefined, { numeric: true }),
      );
  }, [entries, query]);
  const results = matches.slice(0, 10);
  const expanded = open;
  useLayoutEffect(() => {
    setActive(-1);
  }, [document]);
  useLayoutEffect(() => {
    // Escape restores the committed label while focus remains in the field.
    // Select that label after rendering so the next character replaces it.
    if (!open && input.current?.matches(":focus")) input.current.select();
  }, [open, selectedEntry?.label]);
  useEffect(() => {
    if (active >= 0 && open)
      list.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active, open]);
  const close = () => {
    setDisclosed(false);
    setOpen(false);
    setQuery("");
    setActive(-1);
  };
  const beginSearch = () => {
    if (open) return;
    setQuery("");
    setActive(-1);
    setOpen(true);
  };
  const choose = (location: MapLocation) => {
    close();
    input.current?.blur();
    onSelect(location);
  };
  return (
    <div
      ref={root}
      className={`venue-map-search${compact ? " venue-map-search-compact" : ""}`}
      data-disclosed={disclosed}
      onKeyDown={(event) => {
        if (compact && disclosed && event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          close();
          toggle.current?.focus({ preventScroll: true });
        }
      }}
      onBlur={(event) => {
        if (!root.current?.contains(event.relatedTarget as Node | null))
          close();
      }}
    >
      {compact && (
        <button
          ref={toggle}
          type="button"
          className="venue-map-search-toggle"
          aria-label={disclosed ? "Close location search" : "Search map"}
          title={disclosed ? "Close location search" : "Search map"}
          aria-expanded={disclosed}
          aria-controls={fieldId}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (disclosed) {
              close();
              toggle.current?.focus({ preventScroll: true });
            } else {
              setDisclosed(true);
              beginSearch();
              onOpen?.();
            }
          }}
        >
          <Search size={20} aria-hidden="true" />
        </button>
      )}
      <div
        id={fieldId}
        className="venue-map-search-field"
        aria-hidden={compact && !disclosed ? true : undefined}
      >
        {!compact && <Search size={17} aria-hidden="true" />}
        <input
          ref={input}
          type="search"
          disabled={compact && !disclosed}
          role="combobox"
          list={undefined}
          aria-label="Find a location"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-activedescendant={
            expanded && active >= 0 && active < results.length
              ? `${listId}-${active}`
              : undefined
          }
          placeholder="Find a booth or location"
          value={expanded ? query : selectedEntry?.label || query}
          autoComplete="off"
          spellcheck={false}
          onFocus={beginSearch}
          onClick={beginSearch}
          onInput={(event) => {
            setQuery(event.currentTarget.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape" && expanded && !compact) {
              event.preventDefault();
              event.stopPropagation();
              close();
            } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              setOpen(true);
              setActive((index) =>
                results.length
                  ? event.key === "ArrowDown"
                    ? (index + 1) % results.length
                    : (index <= 0 ? results.length : index) - 1
                  : -1,
              );
            } else if (event.key === "Enter" && expanded && results.length) {
              event.preventDefault();
              choose(results[Math.max(0, active)].location);
            }
          }}
        />
        {expanded && query && (
          <button
            type="button"
            aria-label="Clear location search"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setQuery("");
              setActive(-1);
              setOpen(true);
              input.current?.focus();
            }}
          >
            <X size={15} aria-hidden="true" />
          </button>
        )}
      </div>
      {expanded && (
        <div className="venue-map-search-panel">
          <div
            ref={list}
            id={listId}
            className="venue-map-search-results"
            role="listbox"
            aria-label="Locations"
          >
            {results.map((entry, index) => (
              <button
                key={entry.location.id}
                id={`${listId}-${index}`}
                type="button"
                role="option"
                tabIndex={-1}
                aria-selected={selectedId === entry.location.id}
                data-active={active === index}
                data-location-id={entry.location.id}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(entry.location)}
              >
                <span className="venue-map-search-result-name">
                  {entry.name}
                </span>
                <span className="venue-map-search-result-detail">
                  {entry.detail}
                </span>
                {selectedId === entry.location.id && (
                  <Check
                    className="venue-map-search-selected"
                    size={16}
                    aria-hidden="true"
                  />
                )}
              </button>
            ))}
          </div>
          <p className="venue-map-search-summary" role="status">
            {matches.length === 0
              ? "No locations found. Try a name or booth number."
              : matches.length > results.length
                ? `${matches.length} locations · type to narrow your search`
                : `${matches.length} ${matches.length === 1 ? "location" : "locations"}`}
          </p>
        </div>
      )}
    </div>
  );
}
